import { afterNextRender, ChangeDetectionStrategy, Component, DestroyRef, ElementRef, Injector, OnDestroy, computed, effect, inject, signal, untracked, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { auditTime, fromEvent } from 'rxjs';
import { BannerComponent } from '../banner/banner.component';
import { MatchFilterComponent } from '../match-filter/match-filter.component';
import { MobileSportsBarComponent } from '../sports-menu/mobile-sports-bar.component';
import { TournamentGroupComponent } from '../tournament-group/tournament-group.component';
import { TournamentGroupSkeletonComponent } from '../tournament-group/tournament-group-skeleton.component';
import { SportScrollService } from '../sport-scroll.service';
import { ScrollMemoryService } from '../scroll-memory.service';
import { SessionService } from '../session.service';
import { SportsbookFacadeService } from '../sportsbook/sportsbook-facade.service';

const TOURNAMENT_GROUP_PAGE_SIZE = 25;
const AUTO_LOAD_REARM_SCROLL_DISTANCE = 120;

@Component({
  selector: 'app-sportsbook-home',
  standalone: true,
  imports: [TranslateModule, BannerComponent, MatchFilterComponent, MobileSportsBarComponent, TournamentGroupComponent, TournamentGroupSkeletonComponent],
  templateUrl: './sportsbook-home.component.html',
  styleUrl: './sportsbook-home.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SportsbookHomeComponent implements OnDestroy {
  readonly sportsbook = inject(SportsbookFacadeService);
  readonly session = inject(SessionService);
  readonly matchDataLoadFailed = this.session.matchesLoadFailed;
  readonly isMatchDataLoading = computed(() => this.session.matchesLoading());
  readonly isMobile = this.session.isMobile;
  readonly loadingGroupRowCounts = [3, 2] as const;
  readonly visibleTournamentGroupCount = signal(TOURNAMENT_GROUP_PAGE_SIZE);
  readonly visibleTournamentGroups = computed(() => this.sportsbook.getTournamentGroups(this.visibleTournamentGroupCount()));
  readonly hasMoreTournamentGroups = computed(() => this.sportsbook.tournamentGroupCount() > this.visibleTournamentGroupCount());
  private readonly router = inject(Router);
  private readonly sportScroll = inject(SportScrollService);
  private readonly sportFilters = viewChild.required<ElementRef<HTMLElement>>('sportFilters');
  private readonly injector = inject(Injector);
  private readonly scrollMemory = inject(ScrollMemoryService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly autoLoadTrigger = viewChild<ElementRef<HTMLElement>>('autoLoadTrigger');
  private nextAutoLoadScrollTop = 0;
  private autoLoadLocked = false;
  private hasInitializedVisibleTournamentGroups = false;

  constructor() {
    effect(() => {
      this.sportsbook.activeFilter();
      this.sportsbook.searchQuery();
      this.session.selectedSports();

      if (!this.hasInitializedVisibleTournamentGroups) {
        this.hasInitializedVisibleTournamentGroups = true;
        const savedVisibleTournamentGroupCount = this.scrollMemory.consumeHomeVisibleTournamentGroupCount();

        if (savedVisibleTournamentGroupCount !== null && !untracked(this.sportScroll.filtersRequested)) {
          this.visibleTournamentGroupCount.set(savedVisibleTournamentGroupCount);
          this.resetAutoLoadState(window.scrollY);
          this.scrollMemory.restoreHomeScrollPosition();
          return;
        }
      }

      this.visibleTournamentGroupCount.set(TOURNAMENT_GROUP_PAGE_SIZE);
      this.resetAutoLoadState(window.scrollY);
    });

    effect(onCleanup => {
      if (!this.sportScroll.filtersRequested() || this.isMatchDataLoading()) {
        return;
      }
      const render = afterNextRender(() => {
        const filters = this.sportFilters().nativeElement;
        filters.focus({ preventScroll: true });
        // The page scroll padding leaves room for the expanded header and a small gap.
        filters.scrollIntoView({
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
          block: 'start', inline: 'nearest'
        });
        this.sportScroll.filtersRequested.set(false);
      }, { injector: this.injector });
      onCleanup(() => render.destroy());
    });

    effect(onCleanup => {
      const trigger = this.autoLoadTrigger()?.nativeElement;

      if (!trigger || !this.hasMoreTournamentGroups() || typeof IntersectionObserver === 'undefined') {
        return;
      }

      const observer = new IntersectionObserver(entries => this.handleAutoLoadIntersection(entries), {
        threshold: 0,
        rootMargin: '0px 0px 160px 0px'
      });
      observer.observe(trigger);

      onCleanup(() => observer.disconnect());
    });

    const initialScrollTop = window.scrollY;
    this.resetAutoLoadState(initialScrollTop);
    this.scrollMemory.rememberHomeScrollPosition(initialScrollTop);

    fromEvent(window, 'scroll')
      .pipe(auditTime(16), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.handleWindowScroll(window.scrollY));
  }

  ngOnDestroy(): void {
    this.scrollMemory.saveHomeVisibleTournamentGroupCount(this.visibleTournamentGroupCount());
    this.scrollMemory.captureHomeScrollPosition();
  }

  openMatchDetail(matchId: number): void {
    void this.router.navigate(['/match', matchId], { state: { fromSportsbookHome: true } });
  }

  protected showMoreTournamentGroups(): void {
    this.visibleTournamentGroupCount.update(current => current + TOURNAMENT_GROUP_PAGE_SIZE);
  }

  private handleWindowScroll(scrollTop: number): void {
    this.scrollMemory.rememberHomeScrollPosition(scrollTop);
  }

  private handleAutoLoadIntersection(entries: readonly IntersectionObserverEntry[]): void {
    const entry = entries[0];
    if (!entry) {
      return;
    }

    if (!entry.isIntersecting) {
      this.autoLoadLocked = false;
      return;
    }

    const scrollTop = window.scrollY;
    if (this.autoLoadLocked || scrollTop < this.nextAutoLoadScrollTop || !this.hasMoreTournamentGroups()) {
      return;
    }

    this.autoLoadLocked = true;
    this.nextAutoLoadScrollTop = scrollTop + AUTO_LOAD_REARM_SCROLL_DISTANCE;
    this.showMoreTournamentGroups();
  }

  private resetAutoLoadState(scrollTop: number): void {
    this.nextAutoLoadScrollTop = scrollTop + AUTO_LOAD_REARM_SCROLL_DISTANCE;
    this.autoLoadLocked = false;
  }
}
